import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { ConfirmModal } from '@/components/ConfirmModal';
import { VenueEditModal, type VenueEditBase } from '../components/VenueEditModal';
import { supabase } from '../lib/supabase';
import { appleMapsPlaceUrl, osmMapPreviewUrl } from '../lib/appleMaps';
import { timeAgo } from '../lib/timeAgo';
import { ExportButton } from '../components/ExportButton';

type StatusFilter = 'pending' | 'all';
type SortMode = 'recent' | 'hits';

const QUEUE_LOAD_LIMIT = 300;
const KNOWN_QUEUE_TYPES = [
  'apple_fallback',
  'cross_reference_borderline',
  'possible_duplicate',
  'alias_conflict',
] as const;

type QueueRow = {
  id: string;
  queue_type: string;
  status: string;
  apple_place_id: string | null;
  candidate_venue_ids: string[];
  distance_meters: number | null;
  name_similarity: number | null;
  user_typed_query: string | null;
  created_at: string;
  resolved_at: string | null;
};

type VenueRow = {
  id: string;
  venue_name_user_input: string | null;
  latitude: number | null;
  longitude: number | null;
  coords_approximate: boolean | null;
  fsq_place_id: string | null;
  apple_place_id: string | null;
  platform_source: string | null;
  apple_hit_count: number | null;
};

const QUEUE_FSQ_CANDIDATES = new Set([
  'cross_reference_borderline',
  'possible_duplicate',
  'alias_conflict',
]);

function pickNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function rpcMissing(message: string, fn: string): boolean {
  const m = message.toLowerCase();
  return m.includes(fn.toLowerCase()) || m.includes('42883') || m.includes('could not find the function');
}

function formatDistance(m: number | null): string {
  if (m == null || !Number.isFinite(m)) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function formatSim(s: number | null): string {
  if (s == null || !Number.isFinite(s)) return '—';
  return `${Math.round(s * 100)}%`;
}

function queueTypeBadgeStyle(qt: string): string {
  switch (qt) {
    case 'apple_fallback':
      return 'border-amber-500/40 bg-amber-500/15 text-amber-200';
    case 'cross_reference_borderline':
      return 'border-violet-500/40 bg-violet-500/15 text-violet-200';
    case 'possible_duplicate':
      return 'border-red-500/40 bg-red-500/15 text-red-200';
    case 'alias_conflict':
      return 'border-cyan-500/40 bg-cyan-500/15 text-cyan-200';
    default:
      return 'border-border bg-white/[0.04] text-muted';
  }
}

export function ReverseSearchScreen() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [noCoordsOnly, setNoCoordsOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [venuesById, setVenuesById] = useState<Map<string, VenueRow>>(new Map());
  const [hitByAppleId, setHitByAppleId] = useState<Map<string, number>>(new Map());

  const [busyQueueId, setBusyQueueId] = useState<string | null>(null);
  const [queueErrors, setQueueErrors] = useState<Record<string, string>>({});

  const [editFromQueueId, setEditFromQueueId] = useState<string | null>(null);
  const [editVenueBase, setEditVenueBase] = useState<VenueEditBase | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);
  // keepId = the FSQ venue to keep. For cross_reference types it's candidate[0];
  // for apple_fallback it's the suggested FSQ neighbor at candidate[1] (BUG 2).
  const [mergeConfirm, setMergeConfirm] = useState<{ row: QueueRow; keepId: string; keepName: string } | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queueQuery = supabase
        .from('venue_admin_review_queue')
        .select(
          'id, queue_type, status, apple_place_id, candidate_venue_ids, distance_meters, name_similarity, user_typed_query, created_at, resolved_at',
        )
        .order('created_at', { ascending: false })
        .limit(300);

      const { data: queueData, error: queueErr } =
        statusFilter === 'pending' ? await queueQuery.eq('status', 'pending') : await queueQuery;
      if (queueErr) throw queueErr;
      const queueRows = (queueData ?? []) as QueueRow[];
      setQueue(queueRows);
      setQueueErrors({});

      // Candidate venues (for name/coords/fsq) — includes apple_hit_count.
      const venueIds = Array.from(
        new Set(
          queueRows
            .flatMap((r) => r.candidate_venue_ids ?? [])
            .filter((v): v is string => typeof v === 'string' && v.length > 0),
        ),
      );
      // Apple-keyed stubs (carry the demand counter) keyed by apple_place_id.
      const appleIds = Array.from(
        new Set(queueRows.map((r) => r.apple_place_id).filter((x): x is string => !!x)),
      );

      const [candRes, hitRes] = await Promise.all([
        venueIds.length
          ? supabase
              .from('venues')
              .select(
                'id, venue_name_user_input, latitude, longitude, coords_approximate, fsq_place_id, apple_place_id, platform_source, apple_hit_count',
              )
              .in('id', venueIds)
          : Promise.resolve({ data: [], error: null } as const),
        appleIds.length
          ? supabase.from('venues').select('apple_place_id, apple_hit_count').in('apple_place_id', appleIds)
          : Promise.resolve({ data: [], error: null } as const),
      ]);
      if (candRes.error) throw candRes.error;
      if (hitRes.error) throw hitRes.error;

      const vMap = new Map<string, VenueRow>();
      for (const v of (candRes.data ?? []) as VenueRow[]) vMap.set(v.id, v);
      setVenuesById(vMap);

      const hMap = new Map<string, number>();
      for (const r of (hitRes.data ?? []) as { apple_place_id: string | null; apple_hit_count: number | null }[]) {
        const a = r.apple_place_id?.trim();
        if (a) hMap.set(a, r.apple_hit_count ?? 0);
      }
      setHitByAppleId(hMap);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load review queue');
      setQueue([]);
      setVenuesById(new Map());
      setHitByAppleId(new Map());
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const hitCountForRow = useCallback(
    (row: QueueRow): number => {
      const fromApple = row.apple_place_id ? hitByAppleId.get(row.apple_place_id.trim()) : undefined;
      if (typeof fromApple === 'number') return fromApple;
      const cand = venuesById.get((row.candidate_venue_ids ?? [])[0] ?? '');
      return cand?.apple_hit_count ?? 0;
    },
    [hitByAppleId, venuesById],
  );

  const toggleType = useCallback((qt: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(qt)) next.delete(qt);
      else next.add(qt);
      return next;
    });
  }, []);

  // Per-type counts over the loaded queue (independent of display filters).
  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of queue) m.set(r.queue_type, (m.get(r.queue_type) ?? 0) + 1);
    return m;
  }, [queue]);

  const candidateNameForRow = useCallback(
    (row: QueueRow): string =>
      venuesById.get((row.candidate_venue_ids ?? [])[0] ?? '')?.venue_name_user_input ?? '',
    [venuesById],
  );

  const displayedQueue = useMemo(() => {
    let list = noCoordsOnly ? queue.filter((r) => r.distance_meters == null) : queue;
    // Type chips: empty selection = all shown (default, unchanged).
    if (typeFilter.size > 0) {
      list = list.filter((r) => typeFilter.has(r.queue_type));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          candidateNameForRow(r).toLowerCase().includes(q) ||
          (r.user_typed_query ?? '').toLowerCase().includes(q) ||
          (r.apple_place_id ?? '').toLowerCase().includes(q),
      );
    }
    if (sortMode === 'hits') {
      list = [...list].sort((a, b) => hitCountForRow(b) - hitCountForRow(a));
    }
    return list;
  }, [queue, noCoordsOnly, typeFilter, search, candidateNameForRow, sortMode, hitCountForRow]);

  const queueCapped = queue.length >= QUEUE_LOAD_LIMIT;

  const exportColumns = useMemo(
    () => [
      { header: 'queue_type', get: (r: QueueRow) => r.queue_type },
      { header: 'status', get: (r: QueueRow) => r.status },
      { header: 'candidate_name', get: (r: QueueRow) => candidateNameForRow(r) },
      { header: 'user_typed_query', get: (r: QueueRow) => r.user_typed_query ?? '' },
      { header: 'apple_place_id', get: (r: QueueRow) => r.apple_place_id ?? '' },
      {
        header: 'fsq_place_id',
        get: (r: QueueRow) => venuesById.get((r.candidate_venue_ids ?? [])[0] ?? '')?.fsq_place_id ?? '',
      },
      {
        header: 'platform_source',
        get: (r: QueueRow) => venuesById.get((r.candidate_venue_ids ?? [])[0] ?? '')?.platform_source ?? '',
      },
      { header: 'hits', get: (r: QueueRow) => hitCountForRow(r) },
      { header: 'distance_meters', get: (r: QueueRow) => r.distance_meters ?? '' },
      { header: 'name_similarity', get: (r: QueueRow) => r.name_similarity ?? '' },
      { header: 'created_at', get: (r: QueueRow) => r.created_at },
    ],
    [candidateNameForRow, venuesById, hitCountForRow],
  );

  async function approveMerge(row: QueueRow, keepId: string) {
    if (!keepId) {
      setQueueErrors((prev) => ({ ...prev, [row.id]: 'No keep venue id for merge' }));
      return;
    }
    setBusyQueueId(row.id);
    setQueueErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    const snapshot = queue;
    setQueue((prev) => prev.filter((r) => r.id !== row.id));
    try {
      const { error: rpcErr } = await supabase.rpc('admin_approve_reverse_search_queue', {
        p_queue_id: row.id,
        p_keep_venue_id: keepId,
      });
      if (rpcErr) throw rpcErr;
      setSavedToast('Merge approved');
    } catch (e) {
      setQueue(snapshot);
      setQueueErrors((prev) => ({ ...prev, [row.id]: e instanceof Error ? e.message : 'Approve failed' }));
    } finally {
      setBusyQueueId(null);
      setMergeConfirm(null);
    }
  }

  async function denyQueue(row: QueueRow) {
    setBusyQueueId(row.id);
    setQueueErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    const snapshot = queue;
    setQueue((prev) => prev.filter((r) => r.id !== row.id));
    try {
      const { error: rpcErr } = await supabase.rpc('admin_reject_reverse_search_queue', { p_queue_id: row.id });
      if (rpcErr) throw rpcErr;
      setSavedToast('Denied');
    } catch (e) {
      setQueue(snapshot);
      setQueueErrors((prev) => ({ ...prev, [row.id]: e instanceof Error ? e.message : 'Deny failed' }));
    } finally {
      setBusyQueueId(null);
    }
  }

  async function dismissQueue(row: QueueRow) {
    setBusyQueueId(row.id);
    setQueueErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    const snapshot = queue;
    setQueue((prev) => prev.filter((r) => r.id !== row.id));
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_resolve_reverse_search_queue', {
        p_queue_id: row.id,
      });
      if (rpcErr) {
        if (rpcMissing(rpcErr.message, 'admin_resolve_reverse_search_queue')) {
          throw new Error('Dismiss is not enabled yet — run `supabase db push` to apply migration 20260759000000.');
        }
        throw rpcErr;
      }
      if (data && (data as { ok?: boolean }).ok === false) {
        throw new Error((data as { error?: string }).error ?? 'Dismiss failed');
      }
      setSavedToast('Dismissed');
    } catch (e) {
      setQueue(snapshot);
      setQueueErrors((prev) => ({ ...prev, [row.id]: e instanceof Error ? e.message : 'Dismiss failed' }));
    } finally {
      setBusyQueueId(null);
    }
  }

  function openEdit(venueId: string, fromQueueId: string | null) {
    const v = venuesById.get(venueId);
    if (!v) return;
    setEditFromQueueId(fromQueueId);
    setEditVenueBase(v as VenueEditBase);
  }

  const statusClass = (key: StatusFilter) =>
    `rounded-md px-3 py-1 text-xs font-medium transition-colors ${
      statusFilter === key ? 'bg-violet-500/25 text-white' : 'text-muted hover:text-white'
    }`;
  const sortClass = (key: SortMode) =>
    `rounded-md px-3 py-1 text-xs font-medium transition-colors ${
      sortMode === key ? 'bg-violet-500/25 text-white' : 'text-muted hover:text-white'
    }`;

  return (
    <div className="admin-page max-w-6xl mx-auto space-y-6">
      <PageHeader
        title="Foursquare review"
        subtitle="Reverse-search queue (apple_fallback / cross_reference / duplicate / alias). Hits = how many times users tapped this Apple place. Identify places via the Apple Maps link only — never copy Apple API data into saved fields."
      />

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      {savedToast ? (
        <div className="rounded-lg border border-green-500/35 bg-green-500/10 px-4 py-2 text-sm text-green-300 flex items-center justify-between">
          <span>{savedToast}</span>
          <button
            type="button"
            onClick={() => setSavedToast(null)}
            className="text-green-300/70 hover:text-green-100 text-xs"
          >
            dismiss
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">Status</span>
          <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
            <button type="button" className={statusClass('pending')} onClick={() => setStatusFilter('pending')}>
              Pending
            </button>
            <button type="button" className={statusClass('all')} onClick={() => setStatusFilter('all')}>
              All
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">Sort</span>
          <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
            <button type="button" className={sortClass('recent')} onClick={() => setSortMode('recent')}>
              Recent
            </button>
            <button type="button" className={sortClass('hits')} onClick={() => setSortMode('hits')}>
              Most hits
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={noCoordsOnly}
            onChange={(e) => setNoCoordsOnly(e.target.checked)}
            className="h-4 w-4 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
          />
          <span className="text-xs text-white/90">No coordinates only</span>
        </label>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="name / query / apple id"
            className="admin-input !w-52 !px-2.5 !text-xs"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-[11px] text-mutedDim hover:text-white"
            >
              clear
            </button>
          ) : null}
        </div>

        <span className="text-[11px] text-mutedDim tabular-nums">{displayedQueue.length} shown</span>

        <ExportButton
          rows={displayedQueue}
          columns={exportColumns}
          filename="reverse_search_queue"
          label="Export CSV"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">Type</span>
        {KNOWN_QUEUE_TYPES.map((qt) => {
          const selected = typeFilter.has(qt);
          return (
            <button
              key={qt}
              type="button"
              onClick={() => toggleType(qt)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                selected ? queueTypeBadgeStyle(qt) : 'border-border bg-surface text-muted hover:text-white'
              }`}
            >
              <span>{qt}</span>
              <span className="tabular-nums text-mutedDim">{typeCounts.get(qt) ?? 0}</span>
            </button>
          );
        })}
        {typeFilter.size > 0 ? (
          <button
            type="button"
            onClick={() => setTypeFilter(new Set())}
            className="text-[11px] text-mutedDim hover:text-white"
          >
            reset
          </button>
        ) : null}
      </div>

      {queueCapped ? (
        <p className="text-[11px] text-amber-300/80">
          Showing first {QUEUE_LOAD_LIMIT} (capped) — refine Status/Type to narrow the queue.
        </p>
      ) : null}

      {loading ? (
        <SkeletonRows rows={6} className="py-4" />
      ) : displayedQueue.length === 0 ? (
        <p className="text-sm text-muted">No queue rows for this filter.</p>
      ) : (
        <ul className="space-y-4">
          {displayedQueue.map((row) => {
            const venueId = (row.candidate_venue_ids ?? [])[0];
            const venue = venueId ? venuesById.get(venueId) : null;
            const venueName = venue?.venue_name_user_input?.trim() || venueId || 'unknown';
            const busy = busyQueueId === row.id;
            const inlineErr = queueErrors[row.id];
            const isFsqCandidateType = QUEUE_FSQ_CANDIDATES.has(row.queue_type);
            const isAppleFallback = row.queue_type === 'apple_fallback';
            const isPending = row.status === 'pending';
            const hits = hitCountForRow(row);
            // BUG 2: apple_fallback rows now carry a suggested FSQ merge target at
            // candidate_venue_ids[1] (the nearest FSQ neighbor). Surface it + a one-click
            // merge that routes through the proven admin_approve_reverse_search_queue.
            const suggestedFsqId = isAppleFallback ? (row.candidate_venue_ids ?? [])[1] : undefined;
            const suggestedFsq = suggestedFsqId ? venuesById.get(suggestedFsqId) : null;
            const suggestedFsqName = suggestedFsq?.venue_name_user_input?.trim() || suggestedFsqId || null;
            const canMergeAppleFallback = !!(suggestedFsqId && suggestedFsq?.fsq_place_id);
            const venueCoords =
              venue && venue.latitude != null && venue.longitude != null
                ? { lat: venue.latitude, lng: venue.longitude }
                : null;

            return (
              <li key={row.id} className="rounded-xl border border-border bg-surface px-5 py-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-block rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${queueTypeBadgeStyle(
                          row.queue_type,
                        )}`}
                      >
                        {row.queue_type}
                      </span>
                      <span className="inline-block rounded-md border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                        {row.status}
                      </span>
                      <span className="inline-block rounded-md border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide tabular-nums text-accent">
                        {hits.toLocaleString()} {hits === 1 ? 'hit' : 'hits'}
                      </span>
                      {venue?.platform_source ? (
                        <span className="inline-block rounded-md border border-border bg-white/[0.04] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                          {venue.platform_source}
                        </span>
                      ) : null}
                    </div>
                    <div className="admin-label-primary text-sm">{venueName}</div>
                    <div className="text-xs text-mutedDim space-y-1">
                      {row.user_typed_query ? (
                        <div>
                          User query: <span className="text-muted">{row.user_typed_query}</span>
                        </div>
                      ) : null}
                      <div className="tabular-nums">
                        Sim: {formatSim(pickNum(row.name_similarity))} · Dist:{' '}
                        {formatDistance(pickNum(row.distance_meters))}
                        {row.distance_meters == null ? (
                          <span className="ml-1 text-amber-300/90">(no coords)</span>
                        ) : null}
                      </div>
                      {isAppleFallback && suggestedFsqName ? (
                        <div>
                          Suggested FSQ:{' '}
                          <span className={canMergeAppleFallback ? 'text-violet-200' : 'text-muted'}>
                            {suggestedFsqName}
                          </span>
                          {!canMergeAppleFallback ? (
                            <span className="ml-1 text-mutedDim">(no fsq id — verify before merge)</span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="font-mono text-[10px] break-all text-mutedDim">
                        apple: {row.apple_place_id ?? '—'}
                      </div>
                      <div>{timeAgo(row.created_at)}</div>
                      {row.apple_place_id ? (
                        <a
                          href={appleMapsPlaceUrl(row.apple_place_id)}
                          className="inline-block text-violet-300/95 hover:text-violet-200 underline underline-offset-2"
                        >
                          Open in Apple Maps
                        </a>
                      ) : null}
                      {venue?.fsq_place_id ? (
                        <div>
                          <a
                            href={`https://foursquare.com/v/${venue.fsq_place_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-violet-300/95 hover:text-violet-200 underline underline-offset-2"
                          >
                            Open in Foursquare
                          </a>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {isPending ? (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {isFsqCandidateType ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              setMergeConfirm({ row, keepId: venueId ?? '', keepName: venueName })
                            }
                            className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
                          >
                            Approve merge
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void denyQueue(row)}
                            className="rounded-md border border-border bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-muted hover:text-white disabled:opacity-50"
                          >
                            Deny
                          </button>
                        </>
                      ) : null}

                      {isAppleFallback ? (
                        <>
                          {canMergeAppleFallback ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                setMergeConfirm({
                                  row,
                                  keepId: suggestedFsqId!,
                                  keepName: suggestedFsqName ?? 'FSQ venue',
                                })
                              }
                              className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
                            >
                              Approve merge → FSQ
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={busy || !venueId}
                            onClick={() => openEdit(venueId!, row.id)}
                            className="admin-btn admin-btn-primary admin-btn-sm"
                          >
                            Edit / save standalone
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void dismissQueue(row)}
                            className="rounded-md border border-border bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-muted hover:text-white disabled:opacity-50"
                          >
                            Dismiss
                          </button>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {venueCoords ? (
                  <a
                    href={osmMapPreviewUrl(venueCoords.lat, venueCoords.lng)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-[11px] text-violet-300/90 hover:text-violet-200 underline underline-offset-2"
                  >
                    Venue map preview
                  </a>
                ) : null}

                {inlineErr ? (
                  <p className="text-sm text-red-300 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
                    {inlineErr}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {mergeConfirm ? (
        <ConfirmModal
          open
          danger
          title="Approve merge?"
          message={`Fold the Apple-keyed stub INTO "${mergeConfirm.keepName}" (deletes the stub, repoints its ratings). queue_type: ${mergeConfirm.row.queue_type}. User query: ${mergeConfirm.row.user_typed_query ?? '—'}. Verify they are the same place via the Apple Maps link first.`}
          confirmLabel="Approve merge"
          cancelLabel="Cancel"
          busy={busyQueueId === mergeConfirm.row.id}
          onConfirm={() => void approveMerge(mergeConfirm.row, mergeConfirm.keepId)}
          onCancel={() => setMergeConfirm(null)}
        />
      ) : null}

      <VenueEditModal
        open={editVenueBase != null}
        base={editVenueBase}
        fromQueueId={editFromQueueId}
        onClose={() => {
          setEditVenueBase(null);
          setEditFromQueueId(null);
        }}
        onSaved={() => {
          setSavedToast('Saved');
          setEditVenueBase(null);
          if (editFromQueueId) {
            const resolvedQueueId = editFromQueueId;
            setQueue((prev) => prev.filter((r) => r.id !== resolvedQueueId));
          }
          setEditFromQueueId(null);
          void load();
        }}
      />
    </div>
  );
}
